import { Card, List, Tooltip, Typography } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import { useTheme } from 'context/ThemeContext';
import Cookies from 'js-cookie';
import { Business } from 'models/Business';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useGetBusinessNameConatinsQuery } from 'redux/services/abbyCadabby/abbyCadabbyApi';
import { setNavSearchValue } from 'redux/slices/businessListSlice';
import { useAppSelector } from 'redux/store';

const { Paragraph, Text } = Typography;

interface Props {
  division: Business['divisions'][number];
  onCustomerSelect?: () => void;
}

// Define styles as a function that takes isDarkMode as a parameter
const getStyles = (isDarkMode: boolean): InlineStylesModel => ({
  card: {
    height: '100%',
    borderRadius: 8,
    backgroundColor: isDarkMode ? 'rgba(57, 56, 60, 0.51)' : 'rgba(255, 255, 255, 0.29)',
    border: isDarkMode ? '1px solid transparent' : '1px solid transparent',
    willChange: 'border-color', // Optimize for border changes
    transition: 'border-color 0.2s ease'
  },
  cardHover: {
    height: '100%',
    borderRadius: 8,
    backgroundColor: isDarkMode ? 'rgba(57, 56, 60, 0.51)' : 'rgba(255, 255, 255, 0.29)',
    border: isDarkMode ? '1px solid rgb(69, 68, 76)' : '1px solid rgb(203, 203, 205)',
    willChange: 'border-color', // Optimize for border changes
    transition: 'border-color 0.2s ease'
  },
  cardBody: {
    padding: 16,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  },
  title: {
    fontSize: 15,
    fontWeight: 500,
    margin: 0,
    color: '#2786fa',
    lineHeight: '1.4',
    transition: 'none' // Disable transitions on text
  },
  mobileTitle: {
    fontSize: 13,
    fontWeight: 500,
    margin: 0,
    color: '#2786fa',
    lineHeight: '1.3',
    transition: 'none' // Disable transitions on text
  },
  erpId: {
    fontSize: 14,
    color: isDarkMode ? '#fff' : '#000',
    marginTop: 4,
    marginBottom: 0,
    transition: 'none' // Disable transitions on text
  },
  mobileErpId: {
    fontSize: 11,
    color: isDarkMode ? '#fff' : '#000',
    marginTop: 2,
    marginBottom: 0,
    transition: 'none' // Disable transitions on text
  }
});

export const DivisionListItem = memo(({ division, onCustomerSelect }: Props): JSX.Element => {
  const [isHovered, setIsHovered] = useState(false);
  const dispatch = useDispatch();
  const { isDarkMode } = useTheme();
  const { xs } = useBreakpoint();
  const isMobile = xs;

  const { abbyCadabbyParams } = useAppSelector((state) => state);
  const { data } = useGetBusinessNameConatinsQuery(abbyCadabbyParams);

  // Get styles based on current theme and memoize to prevent recalculation
  const styles = useMemo(() => getStyles(isDarkMode), [isDarkMode]);

  // Memoize the card style to prevent recalculation on hover
  const cardStyle = useMemo(() => (isHovered ? styles.cardHover : styles.card), [isHovered, styles.cardHover, styles.card]);

  // Memoize the body style to prevent recalculation
  const bodyStyle = useMemo(
    () => ({
      ...styles.cardBody,
      padding: isMobile ? 10 : 16
    }),
    [styles.cardBody, isMobile]
  );

  // Debounce hover state changes to prevent rapid updates
  const handleMouseEnter = useCallback(() => {
    requestAnimationFrame(() => setIsHovered(true));
  }, []);

  const handleMouseLeave = useCallback(() => {
    requestAnimationFrame(() => setIsHovered(false));
  }, []);

  const handleClick = useCallback((): void => {
    // Early return if no data
    if (!data?.data) return;

    // Find parent business once
    const parentBusiness = data.data.find((buss) => buss.divisions.find((div) => div.id === division.id));
    if (!parentBusiness) return;

    // Prepare customer data before any state changes
    const selectedCustomer = {
      selectedCustomer: {
        business: {
          dataAreaId: parentBusiness.dataAreaId,
          erpId: parentBusiness.erpId,
          id: parentBusiness.id,
          name: parentBusiness.name
        },
        name: division.name,
        erpId: division.erpId,
        id: division.id
      }
    };

    // Set cookie first, outside of animation frame
    const cookieOptions = process.env.REACT_APP_COOKIE_PREFIX === 'localdev' ? { expires: 365 } : { expires: 365, domain: 'mdsiinc.com' };

    try {
      Cookies.set(`${process.env.REACT_APP_COOKIE_PREFIX}_acuity_context`, JSON.stringify(selectedCustomer), cookieOptions);

      // Then batch UI updates
      requestAnimationFrame(() => {
        dispatch(setNavSearchValue(''));
        if (onCustomerSelect) {
          onCustomerSelect();
        }
      });
    } catch (error) {
      console.error('Error during division selection:', error);
    }
  }, [data?.data, division, dispatch, onCustomerSelect]);

  return (
    <List.Item style={{ marginBottom: isMobile ? 6 : 12, height: isMobile ? 70 : 100 }}>
      <Card style={cardStyle} bodyStyle={bodyStyle} hoverable onClick={handleClick} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <Tooltip title={division.name} placement="top">
          <Paragraph style={isMobile ? styles.mobileTitle : styles.title} ellipsis={{ rows: isMobile ? 1 : 2 }}>
            {division.name}
          </Paragraph>
        </Tooltip>
        <Text style={isMobile ? styles.mobileErpId : styles.erpId}>{division.erpId}</Text>
      </Card>
    </List.Item>
  );
});
